
  /**
   * Bracket Calculator Custom Element
   * Calculates optimal bracket size based on countertop overhang and wall framing depth
   */
  class BracketCalculator extends HTMLElement {
    constructor() {
      super();

      // Available bracket sizes (in inches) - these would typically come from product variants
      const sizeOptionsAttr = this.getAttribute('data-size-options');
      
      if (sizeOptionsAttr) {
        this.availableSizes = sizeOptionsAttr.split(',').map(Number);
      } else {
        // Fallback to default sizes if attribute is missing
        this.availableSizes = [6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30];
      }

      // Cache DOM elements
      this.overhangInput = null;
      this.framingInput = null;
      this.widthInput = null;
      this.spacingInput = null;
      this.customSpacingContainer = null;
      this.customSpacingRange = null;
      this.spacingValueDisplay = null;
      this.resultDiv = null;
      this.infoMessage = null;
      this.totalDepthCalc = null;
      this.bracketCalc = null;
      this.recommendation = null;
      this.quantityRecommendation = null;
      this.overhangError = null;
      this.framingError = null;
      this.widthError = null;
      this.spacingError = null;
      
      // Product selection elements
      this.productSelection = null;
      this.colorOptions = null;
      this.selectedProductTitle = null;
      this.selectedQuantity = null;
      this.selectedPrice = null;
      this.addToCartBtn = null;
      
      // Product data
      this.variants = [];
      this.selectedVariant = null;
      this.recommendedSize = null;
      this.recommendedQuantity = null;
      
      // Flag to prevent concurrent add to cart operations
      this.isAddingToCart = false;
      
      // Track current spacing selection
      this.currentSpacingSelection = '16'; // Default to 16" spacing
    }

    connectedCallback() {
      this.initializeElements();
      this.attachEventListeners();
      this.initializeCustomSpacing();
    }

    /**
     * Initialize DOM element references
     */
    initializeElements() {
      this.overhangInput = this.querySelector('#overhang-input');
      this.framingInput = this.querySelector('#framing-input');
      this.widthInput = this.querySelector('#countertop-width');
      this.spacingInput = this.querySelector('#spacing-input');
      this.customSpacingContainer = this.querySelector('#custom-spacing-container');
      this.customSpacingRange = this.querySelector('#custom-spacing-range');
      this.spacingValueDisplay = this.querySelector('#spacing-value');
      this.resultDiv = this.querySelector('#calculation-result');
      this.infoMessage = this.querySelector('#info-message');
      
      // Use more specific selectors for the new markup structure
      // Get elements from the definition list (dl) section
      const dlElements = this.querySelector('dl');
      if (dlElements) {
        this.totalDepthCalc = dlElements.querySelector('dd[id="total-depth-calc"]');
        this.bracketCalc = dlElements.querySelector('dd[id="bracket-calc"]');
        this.recommendation = dlElements.querySelector('dd[id="recommendation"]');
        this.quantityRecommendation = dlElements.querySelector('dd[id="quantity-recommendation"]');
      }
    
      
      this.overhangError = this.querySelector('#overhang-error');
      this.framingError = this.querySelector('#framing-error');
      this.widthError = this.querySelector('#width-error');
      this.spacingError = this.querySelector('#spacing-error');
      
      // Oversize message elements
      this.oversizeMessage = this.querySelector('#oversize-message');
      this.oversizeRequiredLength = this.querySelector('#oversize-required-length');
      
      // Product selection elements
      this.productSelection = this.querySelector('#product-selection');
      this.colorOptions = this.querySelector('#color-options');
      this.selectedProductTitle = this.querySelector('#selected-product-title');
      this.selectedQuantity = this.querySelector('#selected-quantity');
      this.selectedPrice = this.querySelector('#selected-price');
      this.addToCartBtn = this.querySelector('#add-to-cart-btn');
      
      // Load variant data
      this.loadVariantData();
    }

    /**
     * Load variant data from script tag
     */
    loadVariantData() {
      const variantScript = this.querySelector('#bracket-calculator-variant-data');
      if (variantScript) {
        try {
          this.variants = JSON.parse(variantScript.textContent);
        } catch (error) {
          console.error('Failed to parse variant data:', error);
          this.variants = [];
        }
      } else {
        console.error('Variant data script not found');
        this.variants = [];
      }
    }

    /**
     * Attach event listeners for real-time calculation
     */
    attachEventListeners() {
      if (this.overhangInput && this.framingInput && this.widthInput && this.spacingInput) {
        this.overhangInput.addEventListener('input', () => {
          this.handleInputChange();
          this.validateInput(this.overhangInput, 'overhang');
        });
        this.framingInput.addEventListener('input', () => {
          this.handleInputChange();
          this.validateInput(this.framingInput, 'framing');
        });
        this.widthInput.addEventListener('input', () => {
          this.handleInputChange();
          this.validateInput(this.widthInput, 'width');
        });
        
        this.overhangInput.addEventListener('blur', () => this.validateInput(this.overhangInput, 'overhang'));
        this.framingInput.addEventListener('blur', () => this.validateInput(this.framingInput, 'framing'));
        this.widthInput.addEventListener('blur', () => this.validateInput(this.widthInput, 'width'));

        // Handle spacing selection through the el-select component
        this.setupSpacingHandlers();

        // Custom spacing range slider
        if (this.customSpacingRange) {
          this.customSpacingRange.addEventListener('input', () => this.handleCustomSpacingChange());
        }
      }
    }

    /**
     * Setup spacing handlers for el-select component
     */
    setupSpacingHandlers() {
      const spacingSelect = this.querySelector('el-select');
      if (spacingSelect) {
        spacingSelect.addEventListener('change', (e) => {
          const selectedValue = e.detail?.value || e.target.value;
          
          // Store the current selection for getSpacingValue method
          this.currentSpacingSelection = selectedValue;
          
          this.handleSpacingSelection(selectedValue);
        });
        
        // Also listen for option clicks as backup
        const options = spacingSelect.querySelectorAll('el-option');
        options.forEach(option => {
          option.addEventListener('click', () => {
            const value = option.getAttribute('value');
            setTimeout(() => {
              // Store the current selection
              this.currentSpacingSelection = value;
              this.handleSpacingSelection(value);
            }, 100);
          });
        });
      }
    }

    /**
     * Handle spacing selection
     */
    handleSpacingSelection(value) {
      if (value === 'Custom') {
        this.customSpacingContainer?.classList.remove('hidden');
      } else {
        this.customSpacingContainer?.classList.add('hidden');
      }
      this.handleInputChange();
    }

    /**
     * Handle input changes and trigger calculation
     */
        handleInputChange() {
      this.clearErrors();
      this.clearProductSelection();

      const overhang = parseFloat(this.overhangInput.value);
      const framing = parseFloat(this.framingInput.value);
      const width = parseFloat(this.widthInput.value);
      const spacing = this.getSpacingValue();
      
      // Validate inputs using dynamic ranges from HTML attributes
      const overhangValid = this.isValidInputRange(
        overhang, 
        parseFloat(this.overhangInput.getAttribute('min')) || 1,
        parseFloat(this.overhangInput.getAttribute('max')) || 48
      );
      const framingValid = this.isValidInputRange(
        framing,
        parseFloat(this.framingInput.getAttribute('min')) || 1,
        parseFloat(this.framingInput.getAttribute('max')) || 12
      );
      const widthValid = this.isValidInputRange(
        width,
        parseFloat(this.widthInput.getAttribute('min')) || 12,
        parseFloat(this.widthInput.getAttribute('max')) || 1000
      );

      // Only calculate if all inputs have valid values
      // Also ensure spacing is a reasonable value (between 4" and 48")
      const spacingValid = spacing && spacing >= 4 && spacing <= 48;
      
      if (overhangValid && framingValid && widthValid && spacingValid) {
        this.calculateBracketSizeAndQuantity(overhang, framing, width, spacing);
      } else {
        if (!spacingValid && spacing) {
          console.error('Invalid spacing value:', spacing, 'Expected range: 4-48 inches');
        }
        // STATE 1: Show default message
        this.showState1();
      }
    }

    /**
     * Initialize custom spacing display value
     */
    initializeCustomSpacing() {
      if (this.customSpacingRange && this.spacingValueDisplay) {
        // Set the display value to match the range input's initial value
        this.spacingValueDisplay.textContent = this.customSpacingRange.value;
      }
    }

    /**
     * Handle custom spacing range change
     */
    handleCustomSpacingChange() {
      const value = this.customSpacingRange.value;
      this.spacingValueDisplay.textContent = value;
      this.handleInputChange();
    }

    /**
     * Get the current spacing value (from dropdown or custom range)
     */
    getSpacingValue() {
      
      if (this.currentSpacingSelection === 'Custom') {
        const customValue = parseFloat(this.customSpacingRange?.value || 16);
        return customValue;
      }
      
      // For preset values, extract just the numeric part
      // Handle cases like '16"', '16 inch', or '16' 
      const cleanedValue = String(this.currentSpacingSelection || '16')
        .replace(/[^0-9.]/g, '') // Remove all non-numeric characters except decimal point
        .trim();
        
      const presetValue = parseFloat(cleanedValue || 16);
      return presetValue;
    }



    /**
     * Validate individual input values
     */
    validateInput(input, type) {
      const value = parseFloat(input.value);
      let errorElement, isValid, minMax;

      // Get min and max values from the input element
      const minValue = parseFloat(input.getAttribute('min')) || 1;
      const maxValue = parseFloat(input.getAttribute('max')) || 1000;
      minMax = `${minValue}-${maxValue}`;

      switch (type) {
        case 'overhang':
          errorElement = this.overhangError;
          isValid = this.isValidInputRange(value, minValue, maxValue);
          break;
        case 'framing':
          errorElement = this.framingError;
          isValid = this.isValidInputRange(value, minValue, maxValue);
          break;
        case 'width':
          errorElement = this.widthError;
          isValid = this.isValidInputRange(value, minValue, maxValue);
          break;
        default:
          return true;
      }

      if (input.value && !isValid) {
        errorElement.textContent = `Please enter a valid measurement between ${minMax} inches.`;
        input.classList.add('error');
        return false;
      }

      errorElement.textContent = '';
      input.classList.remove('error');
      return true;
    }

    /**
     * Check if input value is valid within specified range
     */
    isValidInputRange(value, min, max) {
      return !isNaN(value) && value >= min && value <= max;
    }

    /**
     * Check if input value is valid (legacy method for backward compatibility)
     */
    isValidInput(value) {
      return !isNaN(value) && value > 0 && value <= 48;
    }

    /**
     * Check if width value is valid (legacy method for backward compatibility)
     */
    isValidWidth(value) {
      return !isNaN(value) && value >= 12 && value <= 1000;
    }

    /**
     * Clear all error messages and styles
     */
    clearErrors() {
      this.overhangError.textContent = '';
      this.framingError.textContent = '';
      this.widthError.textContent = '';
      this.spacingError.textContent = '';
      this.overhangInput.classList.remove('error');
      this.framingInput.classList.remove('error');
      this.widthInput.classList.remove('error');
      this.spacingInput.classList.remove('error');
    }

    /**
     * Filter variants by recommended bracket size
     */
    getVariantsForSize(size) {
      return this.variants.filter(variant => {
        // Check if variant has the exact size in its options
        return variant.title.some(option => {
          // Exact match for size with quotes (e.g., "8"")
          if (option === size + '"') return true;
          
          // Exact match for size with "inch" (e.g., "8 inch")
          if (option === size + ' inch') return true;
          
          // Exact match for just the number as string
          if (option === size.toString()) return true;
          
          // Check for exact match in patterns like "Size: 8"" or "8\" Bracket"
          const sizePattern = new RegExp(`\\b${size}"\\b|\\b${size}\\s+inch\\b`);
          return sizePattern.test(option);
        });
      });
    }

    /**
     * Show product selection interface
     */
    showProductSelection(recommendedSize, quantity) {
      this.recommendedSize = recommendedSize;
      this.recommendedQuantity = quantity;
      
      // Check if pricing should be hidden
      const hidePricing = this.getAttribute('data-hide-price') === 'true';
      if (hidePricing) {
        // Don't show product selection if pricing is hidden
        return;
      }
      
      const sizeVariants = this.getVariantsForSize(recommendedSize);
      
      if (sizeVariants.length === 0) {
        console.warn('No variants found for recommended size');
        return;
      }

      // Clear existing color options and reset selection
      this.colorOptions.innerHTML = '';
      this.selectedVariant = null;
      this.addToCartBtn.disabled = true;
      
      // Create color option radio buttons
      let isFirstOption = true;
      sizeVariants.forEach(variant => {
        if (variant.available) {
          const colorName = this.extractColorFromVariant(variant);
          const radioLabel = this.createColorButton(variant, colorName, isFirstOption);
          this.colorOptions.appendChild(radioLabel);
          
          // Auto-select the first available option
          if (isFirstOption) {
            const radioInput = radioLabel.querySelector('input[type="radio"]');
            radioInput.checked = true;
            this.selectVariant(variant, colorName, radioLabel);
            isFirstOption = false;
          }
        }
      });

      // Update quantity display
      this.selectedQuantity.textContent = quantity;
      
      // Show the product selection section
      this.productSelection.classList.remove('hidden');
    }

    /**
     * Extract color from variant title
     */
    extractColorFromVariant(variant) {
      // Assuming color is usually the second option or the option that's not the size
      const sizeStr = this.recommendedSize + '"';
      return variant.title.find(option => !option.includes(sizeStr)) || variant.title[1] || 'Default';
    }

    /**
     * Create color selection radio button
     */
    createColorButton(variant, colorName, isDefault = false) {
      const label = document.createElement('label');
      label.setAttribute('aria-label', colorName);
      label.setAttribute('aria-description', `$${(variant.price / 100).toFixed(2)} per bracket`);
      label.className = 'group relative flex rounded-lg border border-gray-400 bg-white/10 p-4 has-checked:outline-2 has-checked:-outline-offset-2 has-checked:outline-indigo-400 has-checked:bg-white/90 has-checked:text-black has-focus-visible:outline-3 has-focus-visible:-outline-offset-1 has-disabled:border-gray-600 has-disabled:bg-gray-800 has-disabled:opacity-25 cursor-pointer hover:border-indigo-400 transition-colors text-white';
      
      const radioInput = document.createElement('input');
      radioInput.type = 'radio';
      radioInput.name = 'bracket-color';
      radioInput.value = variant.id;
      radioInput.className = 'absolute inset-0 appearance-none focus:outline-none';
      radioInput.checked = isDefault;
      
      const contentDiv = document.createElement('div');
      contentDiv.className = 'flex-1';
      
      const colorSpan = document.createElement('span');
      colorSpan.className = 'calculator-label-span block !text-base !font-medium';
      colorSpan.textContent = colorName;
      
      const priceSpan = document.createElement('span');
      priceSpan.className = 'calculator-label-span mt-1 block !font-normal !text-sm';
      priceSpan.textContent = `$${(variant.price / 100).toFixed(2)} per bracket`;
      
      contentDiv.appendChild(colorSpan);
      contentDiv.appendChild(priceSpan);
      
      label.appendChild(radioInput);
      label.appendChild(contentDiv);
      
      radioInput.addEventListener('change', () => {
        if (radioInput.checked) {
          this.selectVariant(variant, colorName, label);
        }
      });
      
      return label;
    }

    /**
     * Select a variant
     */
    selectVariant(variant, colorName, buttonElement) {
      this.selectedVariant = variant;
      
      // Update UI
      this.updateVariantSelection(variant, colorName, buttonElement);
      
      // Enable add to cart button
      this.addToCartBtn.disabled = false;
    }

    /**
     * Update variant selection UI
     */
    updateVariantSelection(variant, colorName, labelElement) {
      // Radio buttons handle their own selection state via CSS :checked
      // No need to manually manage styling
      
      // Update product summary
      this.selectedProductTitle.textContent = `${this.recommendedSize}" Hidden Flat Bracket - ${colorName}`;
      this.selectedPrice.textContent = `$${((variant.price * this.recommendedQuantity) / 100).toFixed(2)}`;
      
      // Add cart event listener if not already added
      if (!this.addToCartBtn.hasAttribute('data-listener-added')) {
        this.addToCartBtn.addEventListener('click', () => this.addToCart());
        this.addToCartBtn.setAttribute('data-listener-added', 'true');
      }
    }

    /**
     * Add selected variant to cart
     */
    async addToCart() {
      if (!this.selectedVariant) {
        console.error('No variant selected');
        return;
      }

      // Prevent concurrent add to cart operations
      if (this.isAddingToCart) {
        return;
      }

      this.isAddingToCart = true;

      // Show loading state
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.querySelector('.loading-text').classList.remove('hidden');
      this.addToCartBtn.querySelector('.default-text').classList.add('hidden');

      try {
        const cartData = {
          id: this.selectedVariant.id,
          quantity: this.recommendedQuantity
        };
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(cartData)
        });

        if (response.ok) {
          // Show success feedback
          this.showCartSuccess();
          
          // Sequence: show message, wait 2 seconds, close dialog, then refresh cart
          setTimeout(() => {
            this.closeCalculatorDialog();
            this.refreshCart();
          }, 2000);
          
          document.dispatchEvent(new CustomEvent('cart:updated'));
        } else {
          throw new Error('Failed to add to cart');
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        this.showCartError();
      } finally {
        // Reset button state and flag
        this.isAddingToCart = false;
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.querySelector('.loading-text').classList.add('hidden');
        this.addToCartBtn.querySelector('.default-text').classList.remove('hidden');
      }
    }

    /**
     * Show cart success feedback
     */
    showCartSuccess() {
      this.addToCartBtn.querySelector('.default-text').textContent = 'Added to Cart!';      
    }

    /**
     * Show cart error feedback
     */
    showCartError() {
      const originalText = this.addToCartBtn.querySelector('.default-text').textContent;
      this.addToCartBtn.querySelector('.default-text').textContent = 'Error - Try Again';
      this.addToCartBtn.classList.add('bg-red-600');
      this.addToCartBtn.classList.remove('bg-indigo-600');
      
      setTimeout(() => {
        this.addToCartBtn.querySelector('.default-text').textContent = originalText;
        this.addToCartBtn.classList.remove('bg-red-600');
        this.addToCartBtn.classList.add('bg-indigo-600');
      }, 3000);
    }

    /**
     * Refresh the entire cart to get latest contents and update all cart displays
     * Uses the global addToCartOk() function from ajax-cart.liquid if available
     */
    refreshCart() {
      try {
        // Check if the global addToCartOk function exists (from ajax-cart.liquid)
        if (typeof addToCartOk === 'function') {
          addToCartOk();
        } else {
          // Fallback: manually update cart count if fetchCart is not available
          this.fallbackUpdateCartCount();
        }
      } catch (error) {
        console.error('Error refreshing cart:', error);
        // Fallback to manual cart count update
        this.fallbackUpdateCartCount();
      }
    }

    /**
     * Close the calculator dialog using the el-dialog hide() method
     */
    closeCalculatorDialog() {
      try {
        // Find the el-dialog element
        const elDialog = document.querySelector('el-dialog');
        if (elDialog && typeof elDialog.hide === 'function') {
          elDialog.hide();
        } else {
          // Fallback: try to find the dialog element and trigger close
          const elDialog = document.querySelector('el-dialog');
          if (elDialog) {
            const closeButton = elDialog.querySelector('[command="close"]');
            if (closeButton) {
              closeButton.click();
            }
          }
        }
      } catch (error) {
        console.error('Error closing calculator dialog:', error);
      }
    }

    /**
     * Fallback method to update cart count if global fetchCart is not available
     */
    async fallbackUpdateCartCount() {
      try {
        const response = await fetch('/cart.js', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const cart = await response.json();
          
          // Update all cart count elements on the page
          const cartCountElements = document.querySelectorAll('.cart_count');
          cartCountElements.forEach(element => {
            element.textContent = cart.item_count;
          });
          
        } else {
          console.error('Failed to fetch cart for count update');
        }
      } catch (error) {
        console.error('Error updating cart count:', error);
      }
    }

    /**
     * Calculate the recommended bracket size and quantity based on inputs
     */
    calculateBracketSizeAndQuantity(overhang, framing, width, spacing) {
      
      // Step 1: Calculate total required depth for bracket size
      const totalDepth = overhang + framing;

      // Step 2: Calculate recommended bracket size (subtract 3" for unsupported overhang)
      const unsupportedOverhang = 3; // Using 3" as per the example
      const requiredBracketLength = totalDepth - unsupportedOverhang;

      // Find the closest available bracket size that meets or exceeds the requirement
      const recommendedSize = this.findClosestBracketSize(requiredBracketLength);
      console.log('Recommended size returned:', recommendedSize);

      // Check if dimensions are too large for available brackets
      if (recommendedSize === null) {
        console.log('TRIGGERING OVERSIZE MESSAGE for required length:', requiredBracketLength);
        // STATE 3: Show oversized message
        this.showState3(requiredBracketLength);
        return;
      }

      // Step 3: Calculate bracket quantity based on spacing logic
      const endBracketInset = 5; // 4"-6" inset from each end, using 5" average
      const endBracketCoverage = endBracketInset * 2; // Total width covered by end brackets
      const remainingWidth = width - endBracketCoverage;

      // Calculate additional brackets needed
      const additionalBrackets = Math.ceil(remainingWidth / spacing);
      let totalBrackets = 2 + additionalBrackets; // 2 end brackets + additional
      
      // Safety check: ensure reasonable quantity (max 100 brackets)
      if (totalBrackets > 100) {
        console.error('Calculated quantity seems unreasonable:', totalBrackets, 'Capping at 100. Check spacing value:', spacing);
        totalBrackets = 100;
      }

      this.displayResults(
        overhang,
        framing,
        width,
        totalDepth,
        requiredBracketLength,
        recommendedSize,
        totalBrackets,
        remainingWidth
      );
    }

    /**
     * Find the closest bracket size that meets the requirements
     */
    findClosestBracketSize(requiredLength) {
      console.log('Finding bracket size for required length:', requiredLength);
      console.log('Available sizes:', this.availableSizes);
     
      // Find the smallest bracket size that is greater than or equal to the required length
      for (let size of this.availableSizes) {
        console.log('Checking size:', size, 'against required:', requiredLength);
        if (size >= requiredLength) {
          console.log('Found suitable size:', size);
          return size;
        }
      }

      // If no bracket is large enough, return null to indicate oversize needed
      console.log('NO BRACKET LARGE ENOUGH! Required:', requiredLength, 'Largest available:', this.availableSizes[this.availableSizes.length - 1]);
      return null;
    }

    /**
     * Display calculation results
     */
    displayResults(
      overhang,
      framing,
      width,
      totalDepth,
      requiredLength,
      recommendedSize,
      totalBrackets,
      remainingWidth
    ) {
      // Update calculation display in the definition list format
      if (this.totalDepthCalc) {
        this.totalDepthCalc.innerHTML = `${totalDepth}" <span class="text-gray-400 italic">(${overhang}" overhang + ${framing}" wall framing)</span>`;
      }
      
      if (this.bracketCalc) {
        this.bracketCalc.innerHTML = `${requiredLength.toFixed(1)}" <span class="text-gray-400 italic">(${totalDepth}" - 3" unsupported overhang)</span>`;
      }
      
      // Update recommendations in the grid section
      if (this.recommendation) {
        this.recommendation.textContent = `${recommendedSize}"`;
      }
      
      if (this.quantityRecommendation) {
        this.quantityRecommendation.innerHTML = `${totalBrackets} brackets <span class="text-gray-400 italic">(includes 2 end brackets)</span>`;
      }

      // STATE 2: Show valid results
      this.showState2();
      
      // Show product selection after calculation
      this.showProductSelection(recommendedSize, totalBrackets);
    }

    /**
     * STATE 1: Show default "fill your fields" message
     */
    showState1() {
      // Show info message
      this.infoMessage.classList.remove('hidden');
      // Hide results
      this.resultDiv.classList.add('hidden');
      this.productSelection?.classList.add('hidden');
      // Hide oversize message
      if (this.oversizeMessage) {
        this.oversizeMessage.setAttribute('hidden', '');
      }
      this.clearProductSelection();
    }

    /**
     * STATE 2: Show valid calculation results
     */
    showState2() {
      // Hide info message
      this.infoMessage.classList.add('hidden');
      // Show results
      this.resultDiv.classList.remove('hidden');
      // Hide oversize message
      if (this.oversizeMessage) {
        this.oversizeMessage.setAttribute('hidden', '');
      }
    }

    /**
     * STATE 3: Show oversized message
     */
    showState3(requiredLength) {
      // Hide info message
      this.infoMessage.classList.add('hidden');
      // Hide results
      this.resultDiv.classList.add('hidden');
      this.productSelection?.classList.add('hidden');
      // Show oversize message
      if (this.oversizeRequiredLength) {
        this.oversizeRequiredLength.textContent = requiredLength.toFixed(1);
      }
      if (this.oversizeMessage) {
        this.oversizeMessage.removeAttribute('hidden');
      }
      this.clearProductSelection();
    }

    /**
     * Clear product selection when inputs change
     */
    clearProductSelection() {
      if (this.colorOptions) {
        this.colorOptions.innerHTML = '';
      }
      if (this.selectedProductTitle) {
        this.selectedProductTitle.textContent = '-';
      }
      if (this.selectedPrice) {
        this.selectedPrice.textContent = '-';
      }
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.removeAttribute('data-listener-added');
      }
      this.selectedVariant = null;
    }


  }

  // Register the custom element
  if (!window.customElements.get('bracket-calculator')) {
    window.customElements.define('bracket-calculator', BracketCalculator);
  }